// src/components/ListingGridFilters.tsx
import { useState, useEffect } from 'react';
import {
	Box,
	Flex,
	Text,
	Spacer,
	Square,
	Icon,
	Tooltip,
	Stack,
	Heading,
	Checkbox,
	HStack,
	Button
} from '@chakra-ui/react';
import SearchBar from '../../SearchBar/SearchBar';
import SortDropdown from '../../SortDropdown/SortDropdown';
import DateSlider from '../../DateSlider/DateSlider';
import NumberSlider from '../../NumberSlider/NumberSlider';
import { roundUpToSignificant } from 'utils/number';
import { FcClearFilters } from 'react-icons/fc';
import { AiOutlineFilter, AiFillFilter } from 'react-icons/ai';
import { ListingSortFilter } from '../types';
import { ListingModel } from 'models';

export interface ListingGridFiltersProps {
	showFilters?: boolean;
	showFiltersLabel?: boolean;
	rawQuery: string;
	onSearch: (q: string) => void;
	displayFilters: boolean;
	setDisplayFilters: (open: boolean) => void;
	sortKey: ListingSortFilter;
	onSortBySelect: (key: ListingSortFilter) => void;
	sortType: 'asc' | 'desc';
	onSortTypeToggle: () => void;
	listings: ListingModel[];
	showTypeFilter?: boolean;
	defaultSaleDateRange?: number[];
	defaultPriceRange?: number[];
	defaultShowOriginal?: boolean;
	defaultShowSet?: boolean;
	showClearOption?: boolean;
	onListingSaleDateRangeSelect?: (minYear: number, maxYear: number) => void;
	onListingPriceRangeSelect?: (minValue: number, maxValue: number) => void;
	onListingShowOriginalToggle?: () => void;
	onListingShowSetToggle?: () => void;
	onResetFilters?: () => void;
}

export default function ListingGridFilters({
	showFilters = true,
	showFiltersLabel = true,
	rawQuery,
	onSearch,
	displayFilters,
	setDisplayFilters,
	sortKey,
	onSortBySelect,
	sortType,
	onSortTypeToggle,
	listings,
	showTypeFilter = false,
	defaultSaleDateRange,
	defaultPriceRange,
	defaultShowOriginal = false,
	defaultShowSet = false,
	showClearOption = false,
	onListingSaleDateRangeSelect = () => { },
	onListingPriceRangeSelect = () => { },
	onListingShowOriginalToggle = () => { },
	onListingShowSetToggle = () => { },
	onResetFilters = () => { },
}: ListingGridFiltersProps) {
	const years = listings
		.map(l => l.getDateYear())
		.filter(y => y > 0 && isFinite(y));
	const minYear = years.length ? Math.min(...years) : 1980;
	const maxYear = years.length ? Math.max(...years) : new Date().getFullYear();

	const prices = listings.map(l => l.rawPrice).filter(p => isFinite(p));
	const minPrice = 0;
	const maxPrice = prices.length
		? Math.min(roundUpToSignificant(Math.max(...prices)), 100000)
		: 50000;

	const [saleRange, setSaleRange] = useState<[number, number]>(() =>
		Array.isArray(defaultSaleDateRange) && defaultSaleDateRange.length === 2
			? [defaultSaleDateRange[0], defaultSaleDateRange[1]]
			: [minYear, maxYear]
	);
	const [priceRange, setPriceRange] = useState<[number, number]>(() =>
		Array.isArray(defaultPriceRange) && defaultPriceRange.length === 2
			? [defaultPriceRange[0], defaultPriceRange[1]]
			: [minPrice, maxPrice]
	);

	useEffect(() => {
		if (!(Array.isArray(defaultSaleDateRange) && defaultSaleDateRange.length === 2)) {
			setSaleRange([minYear, maxYear]);
		}
	}, [minYear, maxYear, defaultSaleDateRange]);

	useEffect(() => {
		if (!(Array.isArray(defaultPriceRange) && defaultPriceRange.length === 2)) {
			setPriceRange([minPrice, maxPrice]);
		}
	}, [minPrice, maxPrice, defaultPriceRange]);

	const stepSize = Math.max(1, (maxPrice - minPrice) / 10);

	const renderHeader = () => {
		if (!showFilters) return null;
		return (
			<Flex
				minW="fit-content"
				align={{ base: 'start', md: 'center' }}
				gap={2}
				mb="40px"
				p={{ base: '20px', md: '8px 0' }}
				direction={{ base: 'column', md: 'row' }}
			>
				{showFiltersLabel && (
					<Box p={2}>
						<Text fontSize="xs">Filters:</Text>
					</Box>
				)}
				<Flex direction={{ base: 'column', md: 'row' }} w="100%" gap={2}>
					<SearchBar onChange={onSearch} defaultValue={rawQuery} />
					<Flex direction="row" gap={2} w="100%">
						<Tooltip label="Filter Results">
							<Square
								size={10}
								border="1px solid"
								borderColor="gray.300"
								cursor="pointer"
								borderRadius="md"
								onClick={() => setDisplayFilters(!displayFilters)}
							>
								<Icon as={displayFilters ? AiFillFilter : AiOutlineFilter} />
							</Square>
						</Tooltip>
						<Spacer />
						<SortDropdown<ListingSortFilter>
							options={ListingSortFilter}
							selectedSortBy={sortKey}
							sortType={sortType}
							onSortBySelect={onSortBySelect}
							onSortTypeToggle={onSortTypeToggle}
						/>
					</Flex>
				</Flex>
			</Flex>
		);
	};

	const renderResetButton = () =>
		showClearOption && (
			<Button leftIcon={<FcClearFilters />} width={'fit-content'} variant="outline" onClick={onResetFilters}>
				Reset Filters
			</Button>
		);

	const renderType = () =>
		showTypeFilter && (
			<Stack gap={2}>
				<Heading size="xs">Types</Heading>
				<HStack gap={4} padding={2}>
					<Checkbox isChecked={defaultShowOriginal} onChange={onListingShowOriginalToggle}>
						<Text fontSize={"sm"}>Original</Text>
					</Checkbox>
					<Checkbox isChecked={defaultShowSet} onChange={onListingShowSetToggle}>
						<Text fontSize={"sm"}>Set</Text>
					</Checkbox>
				</HStack>
			</Stack>
		);

	const renderDate = () => (
		<Stack gap={2}>
			<Heading size="xs">Sale Year</Heading>
			<DateSlider
				rangeMin={minYear}
				startingMinDateYear={saleRange[0]}
				startingMaxDateYear={saleRange[1]}
				dropdownDisplay={false}
				onYearRangeSelect={(min, max) => {
					setSaleRange([min, max]);
					onListingSaleDateRangeSelect(min, max);
				}}
			/>
		</Stack>
	);

	const renderPrice = () => (
		<Stack gap={2}>
			<Heading size="xs">Sale Price</Heading>
			<NumberSlider
				dropdownDisplay={false}
				rangeMin={minPrice}
				rangeMax={maxPrice}
				startingMin={priceRange[0]}
				startingMax={priceRange[1]}
				step={stepSize}
				onNumberRangeSelect={(min, max) => {
					setPriceRange([min, max]);
					onListingPriceRangeSelect(min, max);
				}}
			/>
		</Stack>
	);

	return (
		<Box mb={10}>
			{renderHeader()}
			{displayFilters && (
				<Stack gap={4} px={{ base: 3, md: 0 }} textAlign={'left'}>
					{renderResetButton()}
					{renderType()}
					{renderDate()}
					{renderPrice()}
				</Stack>
			)}
		</Box>
	);
}
